import React, { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { injectLibraryApi, exercisesApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, Plus, Search } from 'lucide-react';

const CATEGORIES = [
  'People',
  'Environment',
  'Pollution',
  'Fire/Explosion',
  'Media',
  'Komoko',
  'Other'
];

export default function InjectDatabasePage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [category, setCategory] = useState('all');
  const [company, setCompany] = useState('all');
  const [search, setSearch] = useState('');

  const [selected, setSelected] = useState({});

  // dialogs
  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const [exercises, setExercises] = useState([]);
  const [exerciseId, setExerciseId] = useState('');
  const [adding, setAdding] = useState(false);

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected]
  );

  const load = async () => {
    try {
      setLoading(true);
      const [listRes, compRes, exRes] = await Promise.all([
        injectLibraryApi.list({ category, company, search, limit: 50000 }),
        injectLibraryApi.listCompanies(),
        exercisesApi.getAll(),
      ]);

      setItems(listRes.items || []);
      setCompanies(compRes.companies || []);
      setExercises(exRes || []);
    } catch (e) {
      toast.error('Failed to load inject database');
    } finally {
      setLoading(false);
    }
  };

  const [hasLoaded, setHasLoaded] = useState(false);
  const [showBlank, setShowBlank] = useState(true);

  // Load companies and exercises on mount, but keep items blank until user selects a filter
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [compRes, exRes] = await Promise.all([
          injectLibraryApi.listCompanies(),
          exercisesApi.getAll(),
        ]);
        setCompanies(compRes.companies || []);
        setExercises(exRes || []);
      } catch (e) {
        console.error('Failed to load initial data');
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, []);

  useEffect(() => {
    // Only load when user has explicitly selected a filter
    if (!hasLoaded) return;
    load();
    // eslint-disable-next-line
  }, [category, company, hasLoaded]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setHasLoaded(true);
    setShowBlank(false);
    await load();
  };

  const toggleAllVisible = (checked) => {
    const next = { ...selected };
    items.forEach((it) => {
      next[it.template_id] = checked;
    });
    setSelected(next);
  };

  const handleApplyFilters = async () => {
    setHasLoaded(true);
    setShowBlank(false);
    await load();
  };

  // When category or company changes, auto-load if not 'all'
  const handleCategoryChange = (val) => {
    setCategory(val);
    if (val !== 'all') {
      setShowBlank(false);
      setHasLoaded(true);
    }
  };

  const handleCompanyChange = (val) => {
    setCompany(val);
    if (val !== 'all') {
      setShowBlank(false);
      setHasLoaded(true);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    try {
      setImporting(true);
      const res = await injectLibraryApi.importFile(importFile);
      toast.success(`Imported ${res.imported} templates`);
      setImportOpen(false);
      setImportFile(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to import');
    } finally {
      setImporting(false);
    }
  };

  const handleAddToExercise = async (e) => {
    e.preventDefault();
    if (!exerciseId) {
      toast.error('Select an exercise');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Select at least one template');
      return;
    }

    try {
      setAdding(true);
      for (const tid of selectedIds) {
        // backend endpoint expects one template_id at a time
        await injectLibraryApi.addToExercise(exerciseId, tid);
      }
      toast.success(`Added ${selectedIds.length} injects to exercise`);
      setAddOpen(false);
      setSelected({});
    } catch (err) {
      toast.error(err.message || 'Failed to add to exercise');
    } finally {
      setAdding(false);
    }
  };

  return (
    <DashboardLayout title="Inject Database">
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={handleCategoryChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Company</Label>
              <Select value={company} onValueChange={handleCompanyChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <form onSubmit={handleSearch} className="space-y-2">
              <Label>Search</Label>
              <div className="flex gap-2">
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search injects..." />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>

          <div className="flex gap-2">
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Import .xlsm
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-secondary">Import Inject Database</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleImport} className="space-y-4">
                  <div className="space-y-2">
                    <Label>File (.xlsm/.xlsx)</Label>
                    <Input type="file" accept=".xlsm,.xlsx" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={importing || !importFile}>{importing ? 'Importing...' : 'Import'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button disabled={selectedIds.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Exercise ({selectedIds.length})
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-secondary">Add Selected Templates</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddToExercise} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Exercise</Label>
                    <Select value={exerciseId} onValueChange={setExerciseId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select exercise" />
                      </SelectTrigger>
                      <SelectContent>
                        {exercises.map((ex) => (
                          <SelectItem key={ex.exercise_id} value={ex.exercise_id}>{ex.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={adding}>{adding ? 'Adding...' : 'Add'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-secondary text-lg">Templates</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-muted-foreground">Loading...</div>
            ) : showBlank && items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                <p className="text-lg mb-2">Select a filter to view templates</p>
                <p className="text-sm">Choose a Category, Company, or search to load inject templates</p>
              </div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">No templates found (import your .xlsm first)</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">Showing {items.length} results</div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={items.every((it) => !!selected[it.template_id])} onCheckedChange={(v) => toggleAllVisible(!!v)} />
                    <span className="text-sm">Select all</span>
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-12 gap-0 bg-muted text-xs font-medium">
                    <div className="col-span-1 px-3 py-2">Sel</div>
                    <div className="col-span-2 px-3 py-2">Category</div>
                    <div className="col-span-2 px-3 py-2">Company</div>
                    <div className="col-span-1 px-3 py-2">Inject ID</div>
                    <div className="col-span-1 px-3 py-2">Obj ID</div>
                    <div className="col-span-5 px-3 py-2">Inject Information</div>
                  </div>

                  <div className="divide-y">
                    {items.map((it) => (
                      <div key={it.template_id} className="grid grid-cols-12 gap-0 text-sm">
                        <div className="col-span-1 px-3 py-2">
                          <Checkbox
                            checked={!!selected[it.template_id]}
                            onCheckedChange={(v) => setSelected({ ...selected, [it.template_id]: !!v })}
                          />
                        </div>
                        <div className="col-span-2 px-3 py-2 truncate">{it.category || '-'}</div>
                        <div className="col-span-2 px-3 py-2 truncate">{it.company || '-'}</div>
                        <div className="col-span-1 px-3 py-2 truncate">{it.inject_id || '-'}</div>
                        <div className="col-span-1 px-3 py-2 truncate">{it.objective_id || '-'}</div>
                        <div className="col-span-5 px-3 py-2 line-clamp-2">{it.inject_information || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
